import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useT } from "../../context/LanguageContext";
import { toast } from "sonner";
import {
  Loader2, Shuffle, Send, Check, X, Trophy, ChevronLeft, Upload,
  AlertTriangle, ListChecks, Users, History as HistoryIcon, Info,
  Smartphone, Monitor, Clock, Flag, LogOut, ShieldAlert, Crown, Lock,
  CheckCircle2,
} from "lucide-react";

/**
 * V2 — World Cup hub.
 *
 *   /v2/world-cup/:platform  →  tabs: Play | My Stats | Standings | Timeline | (Admin)
 *
 * Backend: /api/worldcup/* (see /app/backend/worldcup.py).
 */
export default function WorldCupV2() {
  const { platform: platformParam } = useParams();
  const { user } = useAuth();
  const { t } = useT();
  const platform = platformParam === "console" ? "console" : "mobile";
  const [tab, setTab] = useState("play");
  const [me, setMe] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/worldcup/me?platform=${platform}`);
      setMe(data);
    } catch {
      setMe({ error: true });
    }
  }, [platform]);

  useEffect(() => { refresh(); }, [refresh]);
  // Heartbeat every 60 s so the player counts as "online" in everyone else's deck.
  useEffect(() => {
    const tk = setInterval(() => { api.post("/worldcup/heartbeat").catch(() => {}); }, 60_000);
    return () => clearInterval(tk);
  }, []);
  // Light polling — refresh state every 15 s so invites/matches/etc. update.
  useEffect(() => {
    const tk = setInterval(refresh, 15_000);
    return () => clearInterval(tk);
  }, [refresh]);

  if (!me) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-6 h-6 animate-spin text-[#FFD700]" />
    </div>
  );
  if (me.error) return (
    <div className="text-center text-sm text-[#71717A] py-20" data-testid="v2-wc-error">
      World Cup unavailable. Try again later.
    </div>
  );

  const PlatformIcon = platform === "console" ? Monitor : Smartphone;
  const platformLabel = platform === "console" ? "Console" : "Mobile";
  const isStaff = me.is_staff;

  const tabs = [
    { id: "play",      label: t("v2.wc.tab.play"),      Icon: Trophy },
    { id: "stats",     label: t("v2.wc.tab.stats"),     Icon: ListChecks },
    { id: "standings", label: t("v2.wc.tab.standings"), Icon: Users },
    { id: "timeline",  label: t("v2.wc.tab.timeline"),  Icon: Clock },
    { id: "rules",     label: t("v2.wc.tab.rules"),     Icon: ShieldAlert },
    ...(isStaff ? [{ id: "admin", label: t("v2.wc.tab.admin"), Icon: Crown }] : []),
  ];

  return (
    <div className="space-y-4 pb-8" data-testid="v2-worldcup-page">
      {/* Header */}
      <header className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#FFD700] flex items-center gap-1.5">
            <PlatformIcon className="w-3 h-3" />
            // world cup · <span className="text-white">{platformLabel}</span>
          </div>
          <h1 className="text-2xl font-extrabold text-white mt-0.5">World Cup</h1>
        </div>
        <Link to="/v2" className="text-[11px] font-mono text-[#71717A] hover:text-white inline-flex items-center gap-1" data-testid="v2-wc-back">
          <ChevronLeft className="w-3 h-3" /> back
        </Link>
      </header>

      {/* Platform switcher */}
      <div className="flex gap-2" data-testid="v2-wc-platform-switcher">
        {[["console", "Console", Monitor], ["mobile", "Mobile", Smartphone]].map(([p, label, Icon]) => (
          <Link
            key={p}
            to={`/v2/world-cup/${p}`}
            data-testid={`v2-wc-platbtn-${p}`}
            className={`flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-full text-[11px] font-extrabold uppercase tracking-widest transition ${
              platform === p
                ? "bg-[#FFD700] text-[#0F172A] shadow-lg"
                : "bg-white/5 text-white/70 hover:bg-white/10"
            }`}
          >
            <Icon className="w-3.5 h-3.5" /> {label}
          </Link>
        ))}
      </div>

      {/* Testing notice */}
      {me.info?.testing_mode && (
        <div
          className="rounded-2xl border border-[#FFD700]/30 bg-[#FFD700]/10 p-3 text-[11px] text-[#FFE99A] leading-relaxed flex items-start gap-2"
          data-testid="v2-wc-testing-notice"
        >
          <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-[#FFD700]" />
          <span>{t("v2.wc.notice.testing")}</span>
        </div>
      )}

      {/* Removed banner */}
      {me.removed_from_league && (
        <div
          className="rounded-2xl border border-red-500/40 bg-red-500/10 p-3 text-[12px] text-red-200 leading-relaxed"
          data-testid="v2-wc-removed-banner"
        >
          <div className="font-bold text-red-100">{t("v2.wc.removed.title")}</div>
          <div className="mt-0.5">{t("v2.wc.removed.body")}</div>
        </div>
      )}

      {/* Warning banner */}
      {!me.removed_from_league && me.warning_active && (
        <div
          className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-3 text-[12px] text-amber-200 leading-relaxed flex items-start gap-2"
          data-testid="v2-wc-warning-banner"
        >
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-amber-300" />
          <span>{t("v2.wc.warning.body")}</span>
        </div>
      )}

      {/* Tab strip */}
      <div className="flex gap-1.5 overflow-x-auto no-scrollbar" data-testid="v2-wc-tabs">
        {tabs.map(({ id, label, Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            data-testid={`v2-wc-tab-${id}`}
            className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-extrabold uppercase tracking-widest transition ${
              tab === id
                ? "bg-[#FFD700] text-[#0F172A] shadow"
                : "bg-white/5 text-white/70 hover:bg-white/10"
            }`}
          >
            <Icon className="w-3 h-3" /> {label}
          </button>
        ))}
      </div>

      {tab === "play"      && <PlayTab me={me} platform={platform} refresh={refresh} t={t} />}
      {tab === "stats"     && <StatsTab me={me} t={t} />}
      {tab === "standings" && <StandingsTab platform={platform} info={me.info} t={t} myId={user?.id} />}
      {tab === "timeline"  && <TimelineTab info={me.info} t={t} />}
      {tab === "rules"     && <RulesTab t={t} />}
      {tab === "admin"     && isStaff && <AdminTab t={t} />}
    </div>
  );
}

/* ───────────────────── PLAY TAB ───────────────────── */
function PlayTab({ me, platform, refresh, t }) {
  // If the player has an active match, ALL of Play is the match panel.
  if (me.active_match) {
    return <MatchPanel match={me.active_match} myId={me.user_id || me.active_match.p1_id} platform={platform} refresh={refresh} t={t} />;
  }
  if (me.removed_from_league) {
    return (
      <div className="rounded-2xl bg-[#12121C] border border-[#1F1F2A] p-6 text-center text-sm text-[#71717A]">
        {t("v2.wc.removed.body")}
      </div>
    );
  }
  if (me.info?.current_week === 4) {
    return (
      <div className="rounded-2xl bg-[#12121C] border border-[#1F1F2A] p-6 text-center" data-testid="v2-wc-champions-locked">
        <Crown className="w-8 h-8 text-[#FFD700] mx-auto mb-2" />
        <div className="text-sm font-bold text-white">{t("v2.wc.champions")}</div>
        <div className="text-[11px] text-[#A1A1AA] mt-1">{t("v2.wc.champions.sub")}</div>
        <div className="text-[10px] uppercase tracking-widest font-bold text-[#FFD700] mt-3 inline-flex items-center gap-1 bg-[#FFD700]/10 border border-[#FFD700]/30 px-2 py-1 rounded-full">
          <Lock className="w-3 h-3" /> {t("v2.wc.soon")}
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <CapAndTimer me={me} t={t} />
      <RevealCard me={me} platform={platform} refresh={refresh} t={t} />
      {me.current_team && (
        <>
          <InvitesPanel platform={platform} refresh={refresh} t={t} />
          <PlayersDeck platform={platform} refresh={refresh} t={t} />
        </>
      )}
    </div>
  );
}

function CapAndTimer({ me, t }) {
  const played = me.games_played_this_week || 0;
  const cap = me.games_cap_per_week || 20;
  const pct = Math.min(100, Math.round((played / cap) * 100));
  return (
    <div className="rounded-2xl bg-[#12121C] border border-[#1F1F2A] p-3 flex items-center justify-between gap-3" data-testid="v2-wc-cap-timer">
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFD700]">
          {t("v2.wc.cap").replace("{played}", String(played)).replace("{cap}", String(cap))}
        </div>
        <div className="mt-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
          <div className="h-full bg-[#FFD700]" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <div className="text-right">
        <div className="text-[9px] font-mono uppercase tracking-widest text-[#71717A]">{t("v2.wc.next.ends")}</div>
        <Countdown seconds={me.info?.seconds_to_next_boundary || 0} />
      </div>
    </div>
  );
}

function Countdown({ seconds }) {
  const [remaining, setRemaining] = useState(seconds);
  useEffect(() => { setRemaining(seconds); }, [seconds]);
  useEffect(() => {
    if (remaining <= 0) return;
    const tk = setInterval(() => setRemaining(r => Math.max(0, r - 1)), 1000);
    return () => clearInterval(tk);
  }, [remaining]);
  const d = Math.floor(remaining / 86400);
  const h = Math.floor((remaining % 86400) / 3600);
  const m = Math.floor((remaining % 3600) / 60);
  const s = remaining % 60;
  return (
    <div className="text-sm font-mono font-bold text-white tabular-nums" data-testid="v2-wc-countdown">
      {d > 0 ? `${d}d ${h}h` : `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}:${String(s).padStart(2,"0")}`}
    </div>
  );
}

function RevealCard({ me, platform, refresh, t }) {
  const [busy, setBusy] = useState(false);
  const team = me.current_team;
  const reveal = async () => {
    setBusy(true);
    try {
      await api.post("/worldcup/reveal", { platform });
      toast.success("Team revealed!");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reveal");
    } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl bg-gradient-to-br from-[#FFD700]/10 to-[#1E3A8A]/10 border border-[#FFD700]/30 p-4" data-testid="v2-wc-reveal-card">
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFD700]">Your team</div>
          {team ? (
            <div className="mt-1 flex items-center gap-2">
              <span className="text-2xl">{team.flag}</span>
              <div>
                <div className="text-sm font-bold text-white">{team.name}</div>
                <div className="text-[10px] font-mono text-[#71717A]">{team.confederation || "—"}</div>
              </div>
            </div>
          ) : (
            <div className="mt-1 text-xs text-[#71717A]">No team yet — reveal to enter the pool.</div>
          )}
        </div>
        <button
          type="button"
          onClick={reveal}
          disabled={busy}
          data-testid="v2-wc-reveal-btn"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#FFD700] text-[#0F172A] text-[11px] font-extrabold uppercase tracking-widest shadow hover:translate-y-[-1px] transition disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Shuffle className="w-3.5 h-3.5" />}
          {team ? t("v2.wc.reveal.again") : t("v2.wc.reveal.cta")}
        </button>
      </div>
    </div>
  );
}

/* ─── Invites + Players Deck ─── */
function InvitesPanel({ platform, refresh, t }) {
  const [invites, setInvites] = useState({ incoming: [], outgoing: [] });
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/worldcup/invites?platform=${platform}`);
      setInvites(data);
    } catch {}
  }, [platform]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const tk = setInterval(load, 10_000); return () => clearInterval(tk); }, [load]);

  const respond = async (id, action) => {
    try {
      const { data } = await api.post(`/worldcup/invites/${id}/${action}`);
      if (action === "accept" && data?.match) {
        toast.success("Matched!");
      } else {
        toast.success(action);
      }
      load();
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  if (!invites.incoming.length && !invites.outgoing.length) return null;
  return (
    <div className="space-y-2" data-testid="v2-wc-invites">
      {invites.incoming.length > 0 && (
        <div className="rounded-2xl bg-[#12121C] border border-[#1F1F2A] p-3 space-y-2">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFD700]">{t("v2.wc.invites.in")}</div>
          {invites.incoming.map(inv => (
            <div key={inv.id} className="flex items-center justify-between gap-2" data-testid={`v2-wc-incoming-${inv.id}`}>
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-8 h-8 rounded-full bg-[#FFD700]/20 flex items-center justify-center text-xs font-mono font-bold text-[#FFD700]">?</div>
                <div className="text-xs">
                  <div className="text-white font-bold">Player #{inv.from_anon_handle}</div>
                  <div className="text-[10px] text-[#71717A] font-mono">anonymous</div>
                </div>
              </div>
              <div className="flex gap-1.5">
                <button onClick={() => respond(inv.id, "accept")} data-testid={`v2-wc-accept-${inv.id}`}
                  className="px-2.5 py-1 rounded-full bg-[#00FF87] text-[#06120A] text-[10px] font-extrabold uppercase tracking-widest"
                >{t("v2.wc.accept")}</button>
                <button onClick={() => respond(inv.id, "decline")} data-testid={`v2-wc-decline-${inv.id}`}
                  className="px-2.5 py-1 rounded-full bg-white/5 text-white/70 text-[10px] font-extrabold uppercase tracking-widest"
                >{t("v2.wc.decline")}</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {invites.outgoing.length > 0 && (
        <div className="rounded-2xl bg-[#12121C] border border-[#1F1F2A] p-3 space-y-2">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFD700]">{t("v2.wc.invites.out")}</div>
          {invites.outgoing.map(inv => (
            <div key={inv.id} className="flex items-center justify-between gap-2" data-testid={`v2-wc-outgoing-${inv.id}`}>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#FFD700]/10 flex items-center justify-center text-xs font-mono font-bold text-[#FFD700]/60">?</div>
                <div className="text-xs">
                  <div className="text-white/80 font-bold">Player #{inv.to_anon_handle}</div>
                  <div className="text-[10px] text-[#71717A] font-mono">pending…</div>
                </div>
              </div>
              <button onClick={() => respond(inv.id, "cancel")} data-testid={`v2-wc-cancel-${inv.id}`}
                className="px-2.5 py-1 rounded-full bg-white/5 text-white/70 text-[10px] font-extrabold uppercase tracking-widest"
              >{t("v2.wc.cancel")}</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PlayersDeck({ platform, refresh, t }) {
  const [players, setPlayers] = useState([]);
  const [busy, setBusy] = useState({});
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/worldcup/players?platform=${platform}`);
      setPlayers(data.players || []);
    } catch {}
  }, [platform]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const tk = setInterval(load, 10_000); return () => clearInterval(tk); }, [load]);

  const invite = async (id) => {
    setBusy(b => ({ ...b, [id]: true }));
    try {
      await api.post("/worldcup/invite", { to_user_id: id, platform });
      toast.success("Invite sent");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send invite");
    } finally {
      setBusy(b => ({ ...b, [id]: false }));
    }
  };

  return (
    <div className="rounded-2xl bg-[#12121C] border border-[#1F1F2A] p-3 space-y-2" data-testid="v2-wc-players-deck">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFD700]">
          {t("v2.wc.players.title")} <span className="text-white/40">· {players.length}</span>
        </div>
      </div>
      {players.length === 0 ? (
        <div className="text-xs text-[#71717A] py-6 text-center">{t("v2.wc.players.empty")}</div>
      ) : (
        <div className="space-y-1.5">
          {players.map(p => (
            <div key={p.id} className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-xl bg-white/5" data-testid={`v2-wc-player-${p.id}`}>
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-8 h-8 rounded-full bg-[#FFD700]/20 flex items-center justify-center text-xs font-mono font-bold text-[#FFD700]">?</div>
                <div>
                  <div className="text-xs font-bold text-white">Player #{p.anon_handle}</div>
                  <div className="text-[10px] font-mono text-emerald-400 flex items-center gap-1">
                    <span className="w-1 h-1 rounded-full bg-emerald-400" /> online
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => invite(p.id)}
                disabled={!!busy[p.id]}
                data-testid={`v2-wc-invite-${p.id}`}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#FFD700] text-[#0F172A] text-[10px] font-extrabold uppercase tracking-widest disabled:opacity-50"
              >
                {busy[p.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                {t("v2.wc.send.invite")}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Match Panel ─── */
function MatchPanel({ match, myId: _myId, platform, refresh, t }) {
  const { user } = useAuth();
  const myId = user?.id;
  const isP1 = match.p1_id === myId;
  const me = isP1 ? match.p1_user : match.p2_user;
  const opp = isP1 ? match.p2_user : match.p1_user;
  const myTeam = isP1 ? match.p1_team : match.p2_team;
  const oppTeam = isP1 ? match.p2_team : match.p1_team;
  const mySubmission = isP1 ? match.p1_submission : match.p2_submission;
  const oppSubmission = isP1 ? match.p2_submission : match.p1_submission;
  const isHost = match.host_id === myId;
  const myScore = isP1 ? match.confirmed_score_p1 : match.confirmed_score_p2;
  const oppScore = isP1 ? match.confirmed_score_p2 : match.confirmed_score_p1;

  // Score form state
  const [my, setMy] = useState("");
  const [opp_, setOpp] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [reportReason, setReportReason] = useState("");

  // Chat
  const [chat, setChat] = useState([]);
  const [chatMsg, setChatMsg] = useState("");
  const chatScroll = useRef(null);
  const loadChat = useCallback(async () => {
    try {
      const { data } = await api.get(`/worldcup/matches/${match.id}/chat`);
      setChat(data.messages || []);
    } catch {}
  }, [match.id]);
  useEffect(() => { loadChat(); }, [loadChat]);
  useEffect(() => { const tk = setInterval(loadChat, 7_000); return () => clearInterval(tk); }, [loadChat]);
  useEffect(() => { chatScroll.current?.scrollTo({ top: 1e9, behavior: "smooth" }); }, [chat]);

  const submit = async () => {
    if (my === "" || opp_ === "") { toast.error("Enter both scores"); return; }
    if (!file) { toast.error("Screenshot is required"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("my_score", String(my));
      fd.append("opp_score", String(opp_));
      fd.append("screenshot", file);
      await api.post(`/worldcup/matches/${match.id}/submit`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Score submitted");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    } finally { setBusy(false); }
  };

  const forfeit = async () => {
    if (!window.confirm("Leave this match? It will count as an automatic 3-0 loss.")) return;
    setBusy(true);
    try {
      await api.post(`/worldcup/matches/${match.id}/forfeit`);
      toast.success("Match ended");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const report = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/worldcup/matches/${match.id}/report`, { reason: reportReason });
      toast.success(`Report submitted (${data.total_reports} total)`);
      setShowReport(false); setReportReason("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const sendChat = async () => {
    const content = chatMsg.trim();
    if (!content) return;
    try {
      await api.post(`/worldcup/matches/${match.id}/chat`, { content });
      setChatMsg(""); loadChat();
    } catch (e) { toast.error(e?.response?.data?.detail || "Chat failed"); }
  };

  return (
    <div className="space-y-3" data-testid="v2-wc-match-panel">
      {/* Match header — two-player VS card with host/guest labels */}
      <div className="rounded-2xl bg-gradient-to-br from-[#FFD700]/10 to-[#1E3A8A]/10 border border-[#FFD700]/30 p-4">
        <div className="flex items-stretch justify-between gap-2">
          <PlayerColumn user={me} team={myTeam} role={isHost ? "HOST" : "GUEST"} t={t} testidPrefix="v2-wc-match-me" />
          <div className="self-center text-[#FFD700] font-extrabold text-lg">VS</div>
          <PlayerColumn user={opp} team={oppTeam} role={isHost ? "GUEST" : "HOST"} t={t} testidPrefix="v2-wc-match-opp" />
        </div>
        {(match.status === "in_progress") && (
          <div className="mt-3 text-[10px] font-mono uppercase tracking-widest text-center text-[#FFE99A]/80">
            {isHost ? t("v2.wc.match.host") : t("v2.wc.match.guest")}
          </div>
        )}
        {match.status !== "in_progress" && (
          <div className="mt-3 text-center text-xs font-bold text-white">
            {match.status === "forfeited" && <span className="text-red-300">FORFEIT — {myScore}-{oppScore}</span>}
            {match.status === "confirmed" && <span className="text-emerald-300">FINAL — {myScore}-{oppScore}</span>}
            {match.status === "disputed" && <span className="text-amber-300">DISPUTED — awaiting admin review</span>}
          </div>
        )}
      </div>

      {/* Submit your score */}
      {match.status === "in_progress" && !mySubmission && (
        <div className="rounded-2xl bg-[#12121C] border border-[#1F1F2A] p-4 space-y-3" data-testid="v2-wc-submit-form">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFD700]">Submit your score</div>
          <div className="flex gap-2">
            <ScoreInput value={my} onChange={setMy} placeholder="my" testId="v2-wc-myscore" />
            <ScoreInput value={opp_} onChange={setOpp} placeholder="opp" testId="v2-wc-oppscore" />
          </div>
          <label className="block">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#71717A] mb-1">Screenshot</div>
            <input
              type="file"
              accept="image/*"
              onChange={e => setFile(e.target.files?.[0] || null)}
              data-testid="v2-wc-screenshot-input"
              className="block w-full text-[11px] text-white file:mr-2 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:text-[10px] file:font-bold file:uppercase file:tracking-widest file:bg-[#FFD700] file:text-[#0F172A]"
            />
          </label>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            data-testid="v2-wc-submit-btn"
            className="w-full inline-flex items-center justify-center gap-1.5 py-2 rounded-full bg-[#FFD700] text-[#0F172A] text-[11px] font-extrabold uppercase tracking-widest disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Submit score
          </button>
          <AutoConfirmDisclaimer
            match={match}
            mySubmitted={!!mySubmission}
            someoneSubmitted={!!(mySubmission || oppSubmission)}
            bothSubmitted={!!(mySubmission && oppSubmission)}
          />
        </div>
      )}

      {/* Waiting for opponent */}
      {match.status === "in_progress" && mySubmission && !oppSubmission && (
        <div className="space-y-2">
          <div className="rounded-2xl bg-[#12121C] border border-[#1F1F2A] p-4 text-center text-xs text-[#71717A]" data-testid="v2-wc-waiting">
            <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-1" />
            You submitted {mySubmission.my_score}-{mySubmission.opp_score}. Waiting for opponent…
          </div>
          <AutoConfirmDisclaimer
            match={match}
            mySubmitted={true}
            someoneSubmitted={true}
            bothSubmitted={false}
          />
        </div>
      )}

      {/* Forfeit + Report */}
      {match.status === "in_progress" && (
        <div className="flex gap-2">
          <button
            type="button" onClick={forfeit}
            data-testid="v2-wc-forfeit-btn"
            className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-full bg-white/5 text-red-300 text-[10px] font-extrabold uppercase tracking-widest hover:bg-red-500/10"
          >
            <LogOut className="w-3.5 h-3.5" /> {t("v2.wc.match.forfeit")}
          </button>
          <button
            type="button" onClick={() => setShowReport(v => !v)}
            data-testid="v2-wc-report-toggle"
            className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-full bg-white/5 text-amber-300 text-[10px] font-extrabold uppercase tracking-widest hover:bg-amber-500/10"
          >
            <Flag className="w-3.5 h-3.5" /> {t("v2.wc.match.report")}
          </button>
        </div>
      )}

      {showReport && (
        <div className="rounded-2xl bg-[#12121C] border border-amber-500/30 p-3 space-y-2" data-testid="v2-wc-report-form">
          <div className="text-[11px] font-bold text-amber-200">{t("v2.wc.report.title")}</div>
          <div className="text-[10px] text-[#A1A1AA] leading-relaxed">{t("v2.wc.report.rules")}</div>
          <textarea
            value={reportReason}
            onChange={e => setReportReason(e.target.value)}
            placeholder="What happened? (optional)"
            data-testid="v2-wc-report-reason"
            maxLength={500}
            className="w-full p-2 rounded-lg bg-black/40 border border-white/10 text-[11px] text-white"
            rows={3}
          />
          <button
            type="button" onClick={report} disabled={busy}
            data-testid="v2-wc-report-submit"
            className="w-full inline-flex items-center justify-center gap-1.5 py-2 rounded-full bg-amber-500 text-[#0F172A] text-[10px] font-extrabold uppercase tracking-widest disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Flag className="w-3.5 h-3.5" />}
            {t("v2.wc.report.submit")}
          </button>
        </div>
      )}

      {/* Chat */}
      <div className="rounded-2xl bg-[#12121C] border border-[#1F1F2A] p-3" data-testid="v2-wc-chat">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFD700] mb-2">Match chat</div>
        <div ref={chatScroll} className="max-h-44 overflow-y-auto space-y-1.5 mb-2 pr-1">
          {chat.length === 0 && <div className="text-[11px] text-[#71717A] text-center py-3">No messages yet — say hi.</div>}
          {chat.map(m => (
            <div key={m.id} className={`flex ${m.user_id === myId ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] px-2.5 py-1.5 rounded-xl text-[11px] ${m.user_id === myId ? "bg-[#FFD700] text-[#0F172A]" : "bg-white/10 text-white"}`}>
                {m.content}
              </div>
            </div>
          ))}
        </div>
        <div className="flex gap-1.5">
          <input
            type="text"
            value={chatMsg}
            onChange={e => setChatMsg(e.target.value)}
            onKeyDown={e => e.key === "Enter" && sendChat()}
            placeholder="Type a message…"
            data-testid="v2-wc-chat-input"
            className="flex-1 px-2 py-1.5 rounded-lg bg-black/40 border border-white/10 text-[11px] text-white"
          />
          <button onClick={sendChat} data-testid="v2-wc-chat-send"
            className="px-3 py-1.5 rounded-lg bg-[#FFD700] text-[#0F172A] text-[10px] font-extrabold uppercase">
            Send
          </button>
        </div>
      </div>
    </div>
  );
}

function AutoConfirmDisclaimer({ match, mySubmitted, someoneSubmitted, bothSubmitted }) {
  const singleAt = match?.single_submitted_at;
  const windowSecs = match?.auto_confirm_window_secs || 24 * 60 * 60;
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!singleAt || bothSubmitted) return;
    const tk = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tk);
  }, [singleAt, bothSubmitted]);
  const secsLeft = singleAt && !bothSubmitted
    ? Math.max(0, Math.floor((new Date(singleAt).getTime() + windowSecs * 1000 - now) / 1000))
    : null;
  const hh = secsLeft != null ? String(Math.floor(secsLeft / 3600)).padStart(2, "0") : "00";
  const mm = secsLeft != null ? String(Math.floor((secsLeft % 3600) / 60)).padStart(2, "0") : "00";
  const ss = secsLeft != null ? String(secsLeft % 60).padStart(2, "0") : "00";
  return (
    <div
      className={`rounded-xl border p-2.5 text-[10.5px] leading-snug ${
        someoneSubmitted && !bothSubmitted
          ? "border-amber-400/50 bg-amber-500/10 text-amber-100"
          : "border-[#FFD700]/30 bg-[#FFD700]/5 text-[#FFE99A]"
      }`}
      data-testid="v2-wc-auto-confirm-disclaimer"
    >
      <div className="flex items-start gap-2">
        <Clock className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="font-bold uppercase tracking-widest text-[9.5px] mb-0.5">24-hour auto-confirm rule</div>
          <div>
            Once one player submits their score, the other has <b>24 hours</b> to submit theirs.
            If the second submission doesn't arrive in time, the first player's score is taken as <b>final</b>.
          </div>
          {someoneSubmitted && !bothSubmitted && secsLeft != null && (
            <div
              className="mt-1.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-black/40 font-mono tabular-nums text-[11px] font-bold"
              data-testid="v2-wc-auto-confirm-countdown"
            >
              {mySubmitted
                ? <>Opponent has <span className="text-amber-300">{hh}:{mm}:{ss}</span> to submit</>
                : <>You have <span className="text-amber-300">{hh}:{mm}:{ss}</span> to submit</>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function PlayerColumn({ user, team, role, t: _t, testidPrefix }) {
  return (
    <div className="flex-1 text-center" data-testid={testidPrefix}>
      <div className="text-2xl">{team?.flag}</div>
      <div className="text-xs font-bold text-white truncate">{team?.name || "—"}</div>
      <div className="text-[10px] font-mono text-[#71717A] truncate">@{user?.username || user?.id?.slice(0,6)}</div>
      <div className={`mt-1 inline-block text-[9px] font-extrabold uppercase tracking-widest px-2 py-[2px] rounded-full ${
        role === "HOST" ? "bg-[#FFD700] text-[#0F172A]" : "bg-white/10 text-white/80"
      }`} data-testid={`${testidPrefix}-role`}>{role}</div>
    </div>
  );
}

function ScoreInput({ value, onChange, placeholder, testId }) {
  return (
    <input
      type="number" min={0} max={99}
      value={value} onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testId}
      className="flex-1 px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-center text-lg font-bold text-white"
    />
  );
}

/* ───────────────────── STATS TAB ───────────────────── */
function StatsTab({ me, t }) {
  const s = me.stats || { games_played: 0, wins: 0, losses: 0, draws: 0, points_total: 0 };
  return (
    <div className="space-y-3" data-testid="v2-wc-stats-tab">
      <CapAndTimer me={me} t={t} />
      <div className="grid grid-cols-2 gap-2">
        <StatCard label="Points" value={s.points_total} accent="#FFD700" testId="v2-wc-stat-points" />
        <StatCard label="Games" value={s.games_played} accent="#60A5FA" testId="v2-wc-stat-games" />
        <StatCard label="Wins" value={s.wins} accent="#00FF87" testId="v2-wc-stat-wins" />
        <StatCard label="Draws" value={s.draws} accent="#A1A1AA" testId="v2-wc-stat-draws" />
        <StatCard label="Losses" value={s.losses} accent="#F87171" testId="v2-wc-stat-losses" />
        <StatCard label="Reports" value={me.reports_received || 0} accent="#FBBF24" testId="v2-wc-stat-reports" />
      </div>
    </div>
  );
}

function StatCard({ label, value, accent, testId }) {
  return (
    <div className="rounded-2xl bg-[#12121C] border border-[#1F1F2A] p-3" data-testid={testId}>
      <div className="text-[9px] font-mono uppercase tracking-widest" style={{ color: accent }}>{label}</div>
      <div className="text-2xl font-extrabold text-white mt-0.5 tabular-nums">{value}</div>
    </div>
  );
}

/* ───────────────────── STANDINGS TAB ───────────────────── */
function StandingsTab({ platform, info, t, myId }) {
  const [rows, setRows] = useState(null);
  const [week, setWeek] = useState(0); // 0 = season
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/worldcup/standings?platform=${platform}&week=${week}`);
        if (!cancelled) setRows(data.standings || []);
      } catch { if (!cancelled) setRows([]); }
    })();
    return () => { cancelled = true; };
  }, [platform, week]);

  if (rows === null) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#FFD700]" /></div>;
  return (
    <div className="space-y-3" data-testid="v2-wc-standings-tab">
      <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
        <WeekPill active={week === 0} onClick={() => setWeek(0)} label="Season" testId="v2-wc-week-all" />
        {(info?.weeks || []).map(w => (
          <WeekPill key={w.week} active={week === w.week} onClick={() => setWeek(w.week)} label={`W${w.week}${w.is_champions ? " 👑" : ""}`} testId={`v2-wc-week-${w.week}`} />
        ))}
      </div>
      {rows.length === 0 ? (
        <div className="rounded-2xl bg-[#12121C] border border-[#1F1F2A] p-6 text-center text-xs text-[#71717A]">{t("v2.wc.standings.empty")}</div>
      ) : (
        <div className="rounded-2xl bg-[#12121C] border border-[#1F1F2A] divide-y divide-white/5 overflow-hidden">
          {rows.map(r => (
            <div key={r.id} className={`flex items-center gap-2 px-3 py-2 ${r.id === myId ? "bg-[#FFD700]/10" : ""}`} data-testid={`v2-wc-row-${r.id}`}>
              <div className={`w-7 text-center text-[10px] font-mono font-bold ${r.qualifies_for_champions ? "text-[#FFD700]" : "text-white/40"}`}>#{r.rank}</div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold text-white truncate">@{r.username || r.id.slice(0,6)}</div>
                <div className="text-[10px] text-[#71717A] font-mono">{r.games_played} P · {r.wins}W {r.draws}D {r.losses}L</div>
              </div>
              <div className="text-right">
                <div className="text-sm font-extrabold text-[#FFD700] tabular-nums">{r.points_total}</div>
                <div className="text-[9px] font-mono text-[#71717A]">GD {r.gd >= 0 ? "+" : ""}{r.gd}</div>
              </div>
              {r.qualifies_for_champions && (
                <div className="text-[8px] font-extrabold uppercase tracking-widest bg-[#FFD700] text-[#0F172A] px-1.5 py-[2px] rounded-full">{t("v2.wc.qualifies")}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WeekPill({ active, onClick, label, testId }) {
  return (
    <button
      type="button" onClick={onClick} data-testid={testId}
      className={`shrink-0 px-3 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-widest ${
        active ? "bg-[#FFD700] text-[#0F172A]" : "bg-white/5 text-white/70 hover:bg-white/10"
      }`}
    >{label}</button>
  );
}

/* ───────────────────── TIMELINE TAB ───────────────────── */
function TimelineTab({ info, t }) {
  const weeks = info?.weeks || [];
  const currentWeek = info?.current_week || 1;
  return (
    <div className="space-y-2" data-testid="v2-wc-timeline-tab">
      {weeks.map(w => {
        const isCurrent = w.week === currentWeek;
        const isPast = w.week < currentWeek;
        const isFuture = w.week > currentWeek;
        const isChamps = w.is_champions;
        return (
          <div
            key={w.week}
            className={`rounded-2xl p-4 border ${
              isChamps ? "border-[#FFD700]/40 bg-gradient-to-br from-[#FFD700]/10 to-[#1E3A8A]/10" :
              isCurrent ? "border-[#FFD700] bg-[#FFD700]/10" :
              isPast    ? "border-white/5 bg-white/[0.02] opacity-60" :
                          "border-white/5 bg-white/[0.02]"
            }`}
            data-testid={`v2-wc-timeline-week-${w.week}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFD700]">
                  {t("v2.wc.week")} {w.week}{isChamps ? ` · ${t("v2.wc.champions")}` : ""}
                </div>
                <div className="text-xs text-white/80 mt-0.5">
                  {new Date(w.start).toLocaleDateString()} → {new Date(w.end).toLocaleDateString()}
                </div>
                {isChamps && <div className="text-[10px] text-[#A1A1AA] mt-1">{t("v2.wc.champions.sub")}</div>}
              </div>
              <div className="shrink-0">
                {isChamps ? (
                  <span className="inline-flex items-center gap-1 px-2 py-[3px] rounded-full bg-[#FFD700] text-[#0F172A] text-[9px] font-extrabold uppercase tracking-widest">
                    <Lock className="w-3 h-3" /> {t("v2.wc.soon")}
                  </span>
                ) : isCurrent ? (
                  <span className="inline-flex items-center gap-1 px-2 py-[3px] rounded-full bg-emerald-500 text-[#06120A] text-[9px] font-extrabold uppercase tracking-widest">ACTIVE</span>
                ) : isPast ? (
                  <span className="inline-flex items-center gap-1 px-2 py-[3px] rounded-full bg-white/10 text-white/60 text-[9px] font-extrabold uppercase tracking-widest">DONE</span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-[3px] rounded-full bg-white/5 text-white/50 text-[9px] font-extrabold uppercase tracking-widest">
                    <Lock className="w-3 h-3" /> LOCKED
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ───────────────────── RULES TAB ───────────────────── */
function RulesTab({ t }) {
  return (
    <div className="space-y-3" data-testid="v2-wc-rules-tab">
      <div className="rounded-2xl bg-[#12121C] border border-[#1F1F2A] p-4 space-y-2">
        <div className="text-sm font-bold text-white">How matchmaking works</div>
        <ul className="text-[12px] text-[#A1A1AA] leading-relaxed space-y-1.5">
          <li>• Step 1 — Reveal a random national team.</li>
          <li>• Step 2 — Browse <strong className="text-white">anonymous</strong> online players in your platform pool.</li>
          <li>• Send an invitation; names and flags stay hidden until both players accept.</li>
          <li>• When matched, identities + teams unlock and <strong className="text-white">all other pending invites auto-expire</strong>.</li>
          <li>• One player is randomly chosen as the <strong className="text-[#FFD700]">HOST</strong> — they create the room. The <strong className="text-white">GUEST</strong> joins.</li>
          <li>• Submit your score + screenshot. Both must agree to confirm. Disagreements go to admin review.</li>
        </ul>
      </div>
      <div className="rounded-2xl bg-[#12121C] border border-amber-500/30 p-4 space-y-2" data-testid="v2-wc-rules-reports">
        <div className="text-sm font-bold text-amber-200 flex items-center gap-1.5">
          <ShieldAlert className="w-4 h-4" /> Reports & discipline
        </div>
        <ul className="text-[12px] text-amber-100/80 leading-relaxed space-y-1.5">
          <li>• <strong>3 reports against you</strong> → you receive a warning notification.</li>
          <li>• <strong>4 reports against you</strong> → automatic removal from the World Cup league this season (no admin needed).</li>
          <li>• Leaving a match after being matched counts as an automatic <strong>3-0 loss</strong>.</li>
          <li>• Use reports only for cheating, abuse, or refusing to play. Misuse may trigger reciprocal action.</li>
          <li>• Disputed scores go to admin review.</li>
        </ul>
      </div>
      <div className="rounded-2xl bg-[#12121C] border border-[#1F1F2A] p-4 space-y-2">
        <div className="text-sm font-bold text-white">Season & calibration</div>
        <ul className="text-[12px] text-[#A1A1AA] leading-relaxed space-y-1.5">
          <li>• 4-week season. Week 1 is active, weeks 2-3 unlock when the prior week ends.</li>
          <li>• Week 4 = <strong className="text-[#FFD700]">Champions Week</strong> tournament — Top 100 mobile / Top 50 console qualify.</li>
          <li>• 20 games per player per week. Weekly reset every Sunday end-of-day (Riyadh, GMT+3).</li>
          <li>• Scoring: Win 3 · Draw 1 · Loss 0.</li>
        </ul>
      </div>
    </div>
  );
}

/* ───────────────────── ADMIN TAB ───────────────────── */
function AdminTab({ t: _t }) {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await api.get("/worldcup/admin/stats");
      setData(r.data);
    } catch (e) {
      toast.error("Admin stats failed");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const restore = async (id) => {
    try {
      await api.post("/worldcup/admin/restore", { user_id: id });
      toast.success("Restored");
      load();
    } catch (e) { toast.error("Restore failed"); }
  };

  if (!data) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#FFD700]" /></div>;
  return (
    <div className="space-y-3" data-testid="v2-wc-admin-tab">
      {/* Pool stats */}
      <div className="grid grid-cols-2 gap-2">
        {["mobile", "console"].map(p => {
          const s = data.pools?.[p] || {};
          return (
            <div key={p} className="rounded-2xl bg-[#12121C] border border-[#1F1F2A] p-3" data-testid={`v2-wc-admin-pool-${p}`}>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFD700]">{p}</div>
              <div className="text-[11px] text-white/80 mt-1 space-y-0.5">
                <div>Active: <span className="font-bold">{s.active_revealers || 0}</span></div>
                <div>In progress: <span className="font-bold">{s.in_progress || 0}</span></div>
                <div>Confirmed: <span className="font-bold">{s.confirmed || 0}</span></div>
                <div>Disputed: <span className="font-bold text-amber-300">{s.disputed || 0}</span></div>
                <div>Forfeited: <span className="font-bold">{s.forfeited || 0}</span></div>
              </div>
            </div>
          );
        })}
      </div>
      {/* Reports */}
      <div className="rounded-2xl bg-[#12121C] border border-[#1F1F2A] p-3">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFD700] mb-2">Recent reports ({data.reports?.length || 0})</div>
        {(data.reports || []).slice(0, 30).map(r => (
          <div key={r.id} className="flex items-center justify-between gap-2 py-1.5 border-t border-white/5 first:border-t-0">
            <div className="text-[11px] text-white">
              <span className="text-amber-300">@{r.reporter?.username || r.reporter_id?.slice(0,6)}</span>
              {" → "}
              <span className="text-red-300">@{r.reported?.username || r.reported_id?.slice(0,6)}</span>
              {r.reported?.wc_removed && <span className="ml-2 text-[9px] uppercase font-bold text-red-400">REMOVED</span>}
            </div>
            {r.reported?.wc_removed && (
              <button onClick={() => restore(r.reported_id)} className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 font-bold uppercase">
                Restore
              </button>
            )}
          </div>
        ))}
        {(data.reports || []).length === 0 && <div className="text-[11px] text-[#71717A] text-center py-3">No reports.</div>}
      </div>
      {/* Disputed */}
      <div className="rounded-2xl bg-[#12121C] border border-[#1F1F2A] p-3">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFD700] mb-2">Disputed matches ({data.disputed?.length || 0})</div>
        {(data.disputed || []).slice(0, 20).map(m => (
          <DisputeRow key={m.id} m={m} reload={load} />
        ))}
        {(data.disputed || []).length === 0 && <div className="text-[11px] text-[#71717A] text-center py-3">No disputes.</div>}
      </div>
    </div>
  );
}

function DisputeRow({ m, reload }) {
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [busy, setBusy] = useState(false);
  const override = async () => {
    setBusy(true);
    try {
      await api.post(`/worldcup/admin/matches/${m.id}/override`, {
        p1_score: parseInt(p1, 10) || 0,
        p2_score: parseInt(p2, 10) || 0,
      });
      toast.success("Override applied");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="py-2 border-t border-white/5 first:border-t-0 space-y-1.5">
      <div className="text-[11px] text-white flex items-center gap-2 flex-wrap">
        <span>@{m.p1_user?.username || m.p1_id.slice(0,6)} ({m.p1_team?.flag}{m.p1_team?.code})</span>
        <span className="text-amber-300">vs</span>
        <span>@{m.p2_user?.username || m.p2_id.slice(0,6)} ({m.p2_team?.flag}{m.p2_team?.code})</span>
      </div>
      <div className="text-[10px] text-[#A1A1AA]">
        P1 submitted: {m.p1_submission?.my_score}-{m.p1_submission?.opp_score} ·
        P2 submitted: {m.p2_submission?.my_score}-{m.p2_submission?.opp_score}
      </div>
      <div className="flex gap-1.5 items-center">
        <input type="number" min={0} max={99} value={p1} onChange={e => setP1(e.target.value)} placeholder="P1"
               className="w-14 px-2 py-1 rounded bg-black/40 border border-white/10 text-xs text-white text-center" />
        <span className="text-white/40">-</span>
        <input type="number" min={0} max={99} value={p2} onChange={e => setP2(e.target.value)} placeholder="P2"
               className="w-14 px-2 py-1 rounded bg-black/40 border border-white/10 text-xs text-white text-center" />
        <button onClick={override} disabled={busy}
          className="ml-auto px-3 py-1 rounded-full bg-[#FFD700] text-[#0F172A] text-[10px] font-extrabold uppercase tracking-widest disabled:opacity-50">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Set final"}
        </button>
      </div>
    </div>
  );
}
